import { type TrackingPixels, trackingPixelsSchema } from "@ternary/contracts"
import { Badge, DescriptionList, Field, Input, type InputProps, Muted } from "@ternary/ui"
import type { ReactElement } from "react"

export type PixelErrors = Partial<Record<keyof TrackingPixels, string>>

/**
 * The three providers, in page order, with each field's copy. `error` names the rule the contract
 * checks (trackingPixelsSchema: Meta 5 to 32 digits, a Google tag such as G-…, TikTok 5 to 64
 * letters and digits), with an ID of the right shape.
 */
export const PIXEL_PROVIDERS: readonly {
  key: keyof TrackingPixels
  label: string
  hint: string
  error: string
  inputMode?: InputProps["inputMode"]
}[] = [
  {
    key: "meta",
    label: "Meta Pixel ID",
    hint: "In Meta Events Manager, under Data sources.",
    error: "Use digits only, such as 123456789012345.",
    inputMode: "numeric",
  },
  {
    key: "google",
    label: "Google tag ID",
    hint: "In Google Analytics, under Data streams.",
    error: "Use a tag ID such as G-NORTHWIND1.",
  },
  {
    key: "tiktok",
    label: "TikTok Pixel ID",
    hint: "In TikTok Events Manager, under Web events.",
    error: "Use letters and digits only, such as CNORTHWIND01.",
  },
]

/** The IDs with the spaces a paste brings trimmed off, as Save sends them. */
export function trimPixels(pixels: TrackingPixels): TrackingPixels {
  return { google: pixels.google.trim(), meta: pixels.meta.trim(), tiktok: pixels.tiktok.trim() }
}

/**
 * What to fix in each ID the contract refuses, by provider. An empty ID is valid: every provider
 * is optional.
 */
export function validatePixels(draft: TrackingPixels): PixelErrors {
  const result = trackingPixelsSchema.safeParse(trimPixels(draft))
  const errors: PixelErrors = {}
  for (const issue of result.error?.issues ?? []) {
    const provider = PIXEL_PROVIDERS.find(({ key }) => key === issue.path[0])
    if (provider) errors[provider.key] = provider.error
  }
  return errors
}

/**
 * The Meta, Google and TikTok pixel ID fields. Each hint says where to find the ID, until the field
 * shows an error in its place. With `saved`, each label's row says whether that ID is Set, and the
 * status describes its input, before the hint. Read-only
 * (`disabled`), they are values: each ID, or "Not set".
 */
export function PixelFields({
  value,
  onChange,
  errors,
  saved,
  disabled = false,
}: {
  value: TrackingPixels
  onChange(value: TrackingPixels): void
  errors: PixelErrors
  /** The saved IDs, whose status shows beside each label. An unsaved edit leaves it alone. */
  saved?: TrackingPixels | undefined
  disabled?: boolean | undefined
}): ReactElement {
  if (disabled) {
    return (
      <DescriptionList
        items={PIXEL_PROVIDERS.map(({ key, label }) => ({
          term: label,
          value: value[key].trim() || <Muted>Not set</Muted>,
        }))}
      />
    )
  }
  return (
    <div className="admin-pixel-fields">
      {PIXEL_PROVIDERS.map(({ key, label, hint, inputMode }) => (
        // An error takes the hint's place, so a field is no taller with one.
        <Field
          aside={
            saved ? (
              saved[key] ? (
                <Badge tone="success">Set</Badge>
              ) : (
                <Badge>Not set</Badge>
              )
            ) : undefined
          }
          asideDescribes
          error={errors[key]}
          hint={hint}
          key={key}
          label={label}
        >
          <Input
            autoComplete="off"
            inputMode={inputMode}
            onChange={(event) => onChange({ ...value, [key]: event.currentTarget.value })}
            spellCheck={false}
            value={value[key]}
          />
        </Field>
      ))}
    </div>
  )
}
